/**
 * Nova Streetwear Storefront Logic
 * Handles animations, quick add, wishlist toggle, and toast notifications
 */

(function () {
  'use strict';

  // --- Scroll Reveal Animations ---
  function initRevealAnimations() {
    const revealItems = document.querySelectorAll('.nova-reveal');
    if (!revealItems.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );
      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }
  }

  // --- Toast Notification ---
  let toastTimeout = null;
  function showNovaToast(message) {
    if (!message) return;
    let toast = document.querySelector('.nova-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'nova-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }

    toast.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <span>${message}</span>
    `;
    toast.style.display = 'flex';

    if (toastTimeout) clearTimeout(toastTimeout);
    toastTimeout = setTimeout(() => {
      toast.style.display = 'none';
    }, 2800);
  }

  // --- Wishlist Management ---
  function getNovaWishlist() {
    try {
      return JSON.parse(localStorage.getItem('nova_streetwear_wishlist') || '[]');
    } catch (e) {
      return [];
    }
  }

  function setNovaWishlist(list) {
    try {
      localStorage.setItem('nova_streetwear_wishlist', JSON.stringify(list));
    } catch (e) {}
  }

  function initWishlist() {
    const saved = getNovaWishlist();
    document.querySelectorAll('.nova-circle-button[data-product-title]').forEach((btn) => {
      const title = btn.getAttribute('data-product-title');
      if (saved.includes(title)) {
        btn.classList.add('is-favorite');
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        const currentList = getNovaWishlist();
        const itemTitle = this.getAttribute('data-product-title') || 'Item';
        const isFav = currentList.includes(itemTitle);

        let updatedList;
        if (isFav) {
          updatedList = currentList.filter((t) => t !== itemTitle);
          this.classList.remove('is-favorite');
          showNovaToast('Removed from wishlist');
        } else {
          updatedList = [...currentList, itemTitle];
          this.classList.add('is-favorite');
          showNovaToast('Saved to wishlist');
        }
        setNovaWishlist(updatedList);
      });
    });
  }

  // --- Quick Add To Cart ---
  function initQuickAdd() {
    document.querySelectorAll('.nova-quick-add[data-variant-id]').forEach((button) => {
      button.addEventListener('click', async function (e) {
        e.preventDefault();
        e.stopPropagation();

        const variantId = this.getAttribute('data-variant-id');
        const productTitle = this.getAttribute('data-product-title') || 'Item';

        if (!variantId) {
          showNovaToast('Please select options on product page');
          const href = this.getAttribute('data-product-url');
          if (href) window.location.href = href;
          return;
        }

        const originalText = this.innerHTML;
        this.innerHTML = '<span>Adding...</span>';
        this.disabled = true;

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: Number(variantId),
              quantity: 1,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            showNovaToast(`${productTitle} added to your bag`);

            // Update theme cart counter if available
            const cartResponse = await fetch('/cart.js');
            const cartData = await cartResponse.json();

            if (window.CartUtils && typeof window.CartUtils.updateCartCount === 'function') {
              window.CartUtils.updateCartCount(cartData.item_count);
            }
            document.querySelectorAll('.cart-count').forEach((el) => {
              el.textContent = cartData.item_count;
            });
            document.querySelectorAll('.nova-bag-count').forEach((el) => {
              el.textContent = String(cartData.item_count).padStart(2, '0');
            });

            // Open theme minicart drawer if available
            const minicart = document.querySelector('mini-cart');
            if (minicart && typeof minicart.open === 'function') {
              minicart.open();
            } else {
              const cartDrawer = document.querySelector('#cart-drawer, .minicart__drawer');
              if (cartDrawer) {
                cartDrawer.classList.add('is-open');
              }
            }
          } else {
            const errData = await response.json();
            showNovaToast(errData.description || 'Could not add to bag');
          }
        } catch (err) {
          showNovaToast('Error adding to bag');
        } finally {
          this.innerHTML = originalText;
          this.disabled = false;
        }
      });
    });
  }

  // --- Initializer ---
  function initNova() {
    initRevealAnimations();
    initWishlist();
    initQuickAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNova);
  } else {
    initNova();
  }

  // Re-init on Shopify Section load in customizer
  document.addEventListener('shopify:section:load', initNova);
  document.addEventListener('shopify:section:select', initNova);
})();
